import React, { useState, useRef, useEffect } from 'react';
import {
  Bell,
  Search,
  Settings,
  User,
  LogOut,
  Loader2,
  HelpCircle,
  SlidersHorizontal
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../ui/tooltip';
import { useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SearchBox from '../Search/SearchBox';
import SupportForm from '../supportForm/supportForm';

export function ApplicationHeader() {
  const location = useLocation();
  const pathname = location.pathname;
  // Check if we're on the applications page or analytics page
  const isApplicationsPage =
    pathname === '/app/intake' || pathname.startsWith('/application/');
  const isAnalyticsPage = pathname === '/app/analytics';

  // Add state for poppers and loading
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const notificationRef = useRef(null);
  const settingsRef = useRef(null);
  const profileRef = useRef(null);

  // Logout handler
  const handleLogout = () => {
    setIsLoading(true);
    setTimeout(() => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setIsLoading(false);
      setProfileAnchorEl(null);
      window.location.href = '/';
    }, 1000);
  };

  // Helper for popover positioning
  const getPopoverStyle = anchorEl => {
    if (!anchorEl) return { display: 'none' };
    const rect = anchorEl.getBoundingClientRect();
    return {
      position: 'absolute',
      top: rect.bottom + window.scrollY + 8,
      left: rect.right + window.scrollX - 220,
      zIndex: 1300,
      minWidth: 220,
      background: '#fff',
      border: '1px solid #eee',
      borderRadius: 8,
      boxShadow: '0 4px 24px 0 rgba(0,0,0,0.08)',
      padding: 0
    };
  };

  const handleHelpSupport = () => {
    setIsModalOpen(true);
  };

  // Close popovers on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        notificationAnchorEl &&
        notificationRef.current &&
        !notificationRef.current.contains(event.target) &&
        notificationAnchorEl !== event.target
      ) {
        setNotificationAnchorEl(null);
      }
      if (
        settingsAnchorEl &&
        settingsRef.current &&
        !settingsRef.current.contains(event.target) &&
        settingsAnchorEl !== event.target
      ) {
        setSettingsAnchorEl(null);
      }
      if (
        profileAnchorEl &&
        profileRef.current &&
        !profileRef.current.contains(event.target) &&
        profileAnchorEl !== event.target
      ) {
        setProfileAnchorEl(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [notificationAnchorEl, settingsAnchorEl, profileAnchorEl]);

  return (
    <header className='border-b sticky top-0 z-10 bg-white shadow-md'>
      <div className='flex items-center justify-between px-4 py-2'>
        <div className='flex items-center gap-2'>
          <img
            src='/img/logo.svg'
            alt='Nativeorange, Inc'
            style={{ width: '7rem', cursor: 'pointer' }}
          />
          <span className='mx-4 text-xl font-bold'>LexAI</span>
        </div>
        {pathname !== '/' && (
          <div className='flex items-center gap-4 mr-12'>
            <div className='relative'>
              <SearchBox />
            </div>
            {/* Notifications */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='relative'
                    onClick={e =>
                      setNotificationAnchorEl(
                        notificationAnchorEl ? null : e.currentTarget
                      )
                    }
                  >
                    <Bell className='h-5 w-5 text-gray-600' />
                    <span className='absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full'></span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Notifications</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Settings */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={e =>
                      setSettingsAnchorEl(
                        settingsAnchorEl ? null : e.currentTarget
                      )
                    }
                  >
                    <Settings className='h-5 w-5 text-gray-600' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Settings</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Profile */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={e =>
                      setProfileAnchorEl(
                        profileAnchorEl ? null : e.currentTarget
                      )
                    }
                  >
                    <User className='h-5 w-5 text-gray-600' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Profile</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Notification Popover */}
            {notificationAnchorEl && (
              <div
                ref={notificationRef}
                style={getPopoverStyle(notificationAnchorEl)}
                tabIndex={0}
              >
                <div
                  className='flex items-center gap-2 px-4 py-3 text-gray-700'
                  tabIndex={-1}
                >
                  <Bell className='h-4 w-4 text-orange-500' />
                  <span>There are no notifications available</span>
                </div>
              </div>
            )}

            {/* Settings Popover */}
            {settingsAnchorEl && (
              <div
                ref={settingsRef}
                style={getPopoverStyle(settingsAnchorEl)}
                tabIndex={0}
              >
                <button
                  className='flex items-center gap-2 w-full px-4 py-3 hover:bg-orange-50 text-gray-700'
                  onClick={handleHelpSupport}
                >
                  <HelpCircle className='h-4 w-4 text-orange-500' />
                  Help & Support
                </button>
              </div>
            )}

            {/* Profile Popover */}
            {profileAnchorEl && (
              <div
                ref={profileRef}
                style={getPopoverStyle(profileAnchorEl)}
                tabIndex={0}
              >
                {(() => {
                  let user = null;
                  try {
                    user = JSON.parse(
                      localStorage.getItem('userDetails') || '{}'
                    );
                  } catch {
                    user = null;
                  }
                  // Always show the profile popover, even if user is missing, but only show details if user exists
                  return (
                    <div
                      className='flex flex-col items-start px-4 py-3'
                      style={{ minWidth: 220 }}
                    >
                      {user && user.name ? (
                        <>
                          <div className='flex items-center gap-3 mb-2'>
                            <div className='w-10 h-10 rounded-full bg-orange-50 border border-orange-200 flex items-center justify-center font-bold text-lg text-orange-600'>
                              {user.name
                                .split(' ')
                                .map(n => n[0])
                                .join('')
                                .toUpperCase()
                                .slice(0, 2)}
                            </div>
                            <div>
                              <div className='font-semibold text-base text-gray-900 flex items-center gap-1'>
                                {user.name}
                              </div>
                              {user.email && (
                                <div className='text-sm text-gray-500'>
                                  {user.email}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className='w-full border-t my-2 border-gray-100'></div>
                        </>
                      ) : (
                        <div className='text-gray-400  mb-2'></div>
                      )}
                      <button
                        className='flex items-center gap-2 w-full px-0 py-2 hover:bg-orange-50 text-orange-600 font-medium rounded-md transition'
                        onClick={handleLogout}
                        disabled={isLoading}
                        style={{
                          justifyContent: 'flex-start',
                          paddingLeft: 0
                        }}
                      >
                        {isLoading ? (
                          <Loader2 className='animate-spin h-4 w-4 text-orange-500' />
                        ) : (
                          <LogOut className='h-4 w-4 text-orange-500' />
                        )}
                        Logout
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>
      {pathname !== '/' && <MainNavigation />}
      <SupportForm isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </header>
  );
}

function MainNavigation() {
  const location = useLocation();
  const pathname = location.pathname;
  const selectedApplicationData = useSelector(
    state => state.intakeData.selectedApplicationData
  );

  const isSelected = path => pathname === path || pathname.startsWith(path);

  return (
    <nav className='flex px-4 py-2 gap-6 border-t'>
      <Link
        to='/app/details'
        className={`flex items-center gap-1 ${isSelected('/app/details')
          ? 'text-[#FF5533] border-b-2 border-[#FF5533]'
          : 'text-gray-600 hover:text-[#FF5533]'
          }`}
      >
        <div
          className={`p-1 rounded ${isSelected('/app/details') ? 'bg-[#FFF0ED]' : 'bg-gray-100'
            }`}
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            className={isSelected('/app/details') ? 'text-[#FF5533]' : ''}
          >
            <path d='M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z'></path>
            <polyline points='9 22 9 12 15 12 15 22'></polyline>
          </svg>
        </div>
        <span>Intake Triage</span>
      </Link>
      <Link
        to={`/app/intake/${selectedApplicationData?.application_id}`}
        className={`flex items-center gap-1 ${isSelected(`/app/intake/${selectedApplicationData?.application_id}`)
          ? 'text-[#FF5533] border-b-2 border-[#FF5533]'
          : 'text-gray-600 hover:text-[#FF5533]'
          }`}
      >
        <div
          className={`p-1 rounded ${isSelected(`/app/intake/${selectedApplicationData?.application_id}`)
            ? 'bg-[#FFF0ED]'
            : 'bg-gray-100'
            }`}
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z'></path>
            <polyline points='14 2 14 8 20 8'></polyline>
            <line x1='16' y1='13' x2='8' y2='13'></line>
            <line x1='16' y1='17' x2='8' y2='17'></line>
            <polyline points='10 9 9 9 8 9'></polyline>
          </svg>
        </div>
        <span>Application Overview</span>
      </Link>
      <Link
        to={`/app/locations/${selectedApplicationData?.application_id}`}
        className={`flex items-center gap-1 ${isSelected(`/app/locations/${selectedApplicationData?.application_id}`)
          ? 'text-[#FF5533] border-b-2 border-[#FF5533]'
          : 'text-gray-600 hover:text-[#FF5533]'
          }`}
      >
        <div
          className={`p-1 rounded ${isSelected('/app/locations') ? 'bg-[#FFF0ED]' : 'bg-gray-100'
            }`}
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <rect x='4' y='4' width='16' height='16' rx='2' ry='2'></rect>
            <path d='M9 9h6v6H9z'></path>
            <path d='M9 1v3'></path>
            <path d='M15 1v3'></path>
            <path d='M9 20v3'></path>
            <path d='M15 20v3'></path>
            <path d='M20 9h3'></path>
            <path d='M20 14h3'></path>
            <path d='M1 9h3'></path>
            <path d='M1 14h3'></path>
          </svg>
        </div>
        <span>Company Analysis</span>
      </Link>
      <Link
        to='/app/analytics'
        className={`flex items-center gap-1 ${isSelected('/app/analytics')
          ? 'text-[#FF5533] border-b-2 border-[#FF5533]'
          : 'text-gray-600 hover:text-[#FF5533]'
          }`}
      >
        <div
          className={`p-1 rounded ${isSelected('/app/analytics') ? 'bg-[#FFF0ED]' : 'bg-gray-100'
            }`}
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            className={isSelected('/app/analytics') ? 'text-[#FF5533]' : ''}
          >
            <circle cx='12' cy='12' r='10'></circle>
            <line x1='12' y1='16' x2='12' y2='12'></line>
            <line x1='12' y1='8' x2='12.01' y2='8'></line>
          </svg>
        </div>
        <span>Analytics</span>
      </Link>
      {/* <Link
        to='/app/operations'
        className={`flex items-center gap-1 ${
          isSelected('/app/operations')
            ? 'text-[#FF5533] border-b-2 border-[#FF5533]'
            : 'text-gray-600 hover:text-[#FF5533]'
        }`}
      >
        <div
          className={`p-1 rounded ${
            isSelected('/app/operations') ? 'bg-[#FFF0ED]' : 'bg-gray-100'
          }`}
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            className={isSelected('/app/operations') ? 'text-[#FF5533]' : ''}
          >
            <circle cx='12' cy='12' r='10'></circle>
            <line x1='12' y1='16' x2='12' y2='12'></line>
            <line x1='12' y1='8' x2='12.01' y2='8'></line>
          </svg>
        </div>
        <span>Operations</span>
      </Link> */}
    </nav>
  );
}
